import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { SupportPlayer, supportPlayerName } from '../../../hooks/supportplayer/useOrgSupportPlayers'
import { IRecruiter } from '../CampaignTable'

interface Option {
  value: string
  label: string
}

interface CampaignAssignSupporterModalProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  recruiters: IRecruiter[]
  supporters: SupportPlayer[]
  submitCallback: (values: {
    user_id: string
    short_user_id: string
    additional: boolean
  }) => void | Promise<void>
}

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: '44px',
    borderRadius: '0.85rem',
    border: '1px solid #e4e6ef',
    backgroundColor: '#ffffff',
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
}

const CampaignAssignSupporterModal: React.FC<CampaignAssignSupporterModalProps> = ({
  showModal,
  setShowModal,
  recruiters,
  supporters,
  submitCallback,
}) => {
  const [selectedSupporter, setSelectedSupporter] = useState<Option | null>(null)
  const [selectedRecruiter, setSelectedRecruiter] = useState<Option | null>(null)
  // Supporter already in the campaign who also supports another participant (e.g. several kids)
  const [additional, setAdditional] = useState(false)

  useEffect(() => {
    if (showModal) return
    setSelectedSupporter(null)
    setSelectedRecruiter(null)
    setAdditional(false)
  }, [showModal])

  // Which participants each supporter is already registered under
  const participantsBySupporter = useMemo(() => {
    const map = new Map<string, Set<string>>()
    recruiters.forEach((r) =>
      r.recruited_users
        .filter((ru) => !!ru.user_id)
        .forEach((ru) => {
          const existing = map.get(ru.user_id) || new Set<string>()
          existing.add(r.short_user_id)
          map.set(ru.user_id, existing)
        })
    )
    return map
  }, [recruiters])

  const supporterOptions = useMemo<Option[]>(
    () =>
      supporters
        .filter((s) => additional === participantsBySupporter.has(s._id))
        .map((s) => ({
          value: s._id,
          label: [supportPlayerName(s) || 'Ukjent navn', s.email].filter(Boolean).join(' — '),
        })),
    [supporters, participantsBySupporter, additional]
  )

  const recruiterOptions = useMemo<Option[]>(
    () =>
      recruiters.map((r) => ({
        value: r.short_user_id,
        label: `${r.name || 'Ukjent'} (${r.short_user_id})`,
      })),
    [recruiters]
  )

  const alreadySupported = selectedSupporter
    ? participantsBySupporter.get(selectedSupporter.value)
    : undefined

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size='lg' keyboard={false}>
      <div className='modal-body'>
        <h4 className='mb-2'>Legg til støttespiller</h4>
        <p className='text-muted fs-7'>
          Velg en støttespiller med aktiv støtte som ikke allerede er i kampanjen, og hvilken
          deltager den skal tilknyttes.
        </p>

        <div className='form-check mt-7'>
          <input
            className='form-check-input'
            type='checkbox'
            id='assignSupporterAdditional'
            checked={additional}
            onChange={(e) => {
              setAdditional(e.target.checked)
              setSelectedSupporter(null)
            }}
          />
          <label className='form-check-label' htmlFor='assignSupporterAdditional'>
            Støttespilleren er allerede i kampanjen og støtter flere deltagere (f.eks. flere barn)
          </label>
        </div>

        <div className='row mb-3 mt-7'>
          <label className='col-lg-3 col-form-label fw-bold fs-6'>Støttespiller:</label>
          <div className='col-lg-9 fv-row'>
            <Select
              styles={selectStyles}
              options={supporterOptions}
              value={selectedSupporter}
              onChange={(opt) => setSelectedSupporter(opt as Option)}
              placeholder='Velg støttespiller...'
              noOptionsMessage={() => 'Ingen tilgjengelige støttespillere'}
              isClearable
            />
          </div>
        </div>

        <div className='row mb-3 mt-7'>
          <label className='col-lg-3 col-form-label fw-bold fs-6'>Deltager:</label>
          <div className='col-lg-9 fv-row'>
            <Select
              styles={selectStyles}
              options={recruiterOptions}
              value={selectedRecruiter}
              onChange={(opt) => setSelectedRecruiter(opt as Option)}
              placeholder='Velg deltager...'
              noOptionsMessage={() => 'Ingen deltagere i kampanjen'}
              formatOptionLabel={(opt) => {
                const option = opt as Option
                if (!alreadySupported?.has(option.value)) return <span>{option.label}</span>
                return (
                  <span className='text-success fw-bold'>
                    {option.label}
                    <span className='badge badge-light-success ms-2'>Støtter allerede</span>
                  </span>
                )
              }}
              isClearable
            />
          </div>
        </div>

        <br />
        <button
          type='button'
          style={{ float: 'right' }}
          disabled={!selectedSupporter || !selectedRecruiter}
          onClick={async () => {
            if (!selectedSupporter || !selectedRecruiter) return
            await submitCallback({
              user_id: selectedSupporter.value,
              short_user_id: selectedRecruiter.value,
              additional,
            })
          }}
          className='btn btn-primary'
        >
          Lagre
        </button>
      </div>
    </Modal>
  )
}

export default React.memo(CampaignAssignSupporterModal)
